import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiLogOut, FiMoon, FiSun, FiCheckSquare, FiBarChart2, FiChevronLeft } from 'react-icons/fi';
import { ThemeContext } from '../context/ThemeContext';
import { AssessorAuthContext } from '../context/AssessorAuthContext';
import { showNotification } from './Notification';
import assessorApi from '../api/assessorApi';
import './Assessment.css';
import './AssessorDashboard.css';

const SIDEBAR_COLLAPSED_KEY = 'tvet_sidebar_collapsed';

const FIELDS = [
  { key: 'workbook', label: 'Workbook' },
  { key: 'personal_statement', label: 'Per. Statmt' },
  { key: 'oral_qa', label: 'Oral Q&A' },
];

const AssessorDashboard = () => {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { assessor, logout, setAssessor } = useContext(AssessorAuthContext);
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true');
  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      return next;
    });
  };

  const [view, setView] = useState('units');
  const [selectedUnit, setSelectedUnit] = useState(1);
  const [trainees, setTrainees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const [reportUnit, setReportUnit] = useState('all');
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/assessor-login', { replace: true });
  };

  const fetchUnit = useCallback(async (unit) => {
    setLoading(true);
    try {
      const res = await assessorApi.get(`/api/assessor-portal/units/${unit}/trainees`);
      setTrainees(res.data.trainees || []);
    } catch (error) {
      showNotification(error.response?.data?.error || 'Error loading unit', 'error');
      setTrainees([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Refresh the profile once on mount in case the admin changed units_count since last login.
    assessorApi
      .get('/api/assessor-portal/me')
      .then((res) => setAssessor(res.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (view === 'units') fetchUnit(selectedUnit);
  }, [view, selectedUnit, fetchUnit]);

  const fetchReport = useCallback(async (unit) => {
    setReportLoading(true);
    try {
      const res = await assessorApi.get(`/api/assessor-portal/report?unit=${unit}`);
      setReport(res.data);
    } catch (error) {
      showNotification(error.response?.data?.error || 'Error loading report', 'error');
      setReport(null);
    } finally {
      setReportLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'report') fetchReport(reportUnit);
  }, [view, reportUnit, fetchReport]);

  const toggleField = async (trainee, field) => {
    const key = `${trainee.id}-${field}`;
    const newValue = !trainee[field];

    setTrainees((prev) => prev.map((t) => (t.id === trainee.id ? { ...t, [field]: newValue } : t)));
    setSavingKey(key);
    try {
      await assessorApi.put(`/api/assessor-portal/units/${selectedUnit}/evidence/${trainee.id}`, {
        field,
        value: newValue,
      });
    } catch (error) {
      // Roll back on failure
      setTrainees((prev) => prev.map((t) => (t.id === trainee.id ? { ...t, [field]: !newValue } : t)));
      showNotification(error.response?.data?.error || 'Error saving — try again', 'error');
    } finally {
      setSavingKey(null);
    }
  };

  const unitsCount = assessor?.units_count || 12;
  const units = Array.from({ length: unitsCount }, (_, i) => i + 1);
  const pageTitle = view === 'report' ? 'My Report' : `Unit ${selectedUnit}`;
  const initials = (assessor?.name || 'A').trim().slice(0, 2).toUpperCase();

  return (
    <div className="layout assessor-layout" data-theme={theme}>
      <header className="header">
        <div className="header-content">
          <div className="header-titles">
            <h1>{pageTitle}</h1>
            <p className="header-breadcrumb">CSS FARMS IDEAS-TVET / Assessor Portal / {pageTitle}</p>
          </div>
          <div className="header-actions">
            <button onClick={toggleTheme} className="theme-toggle" title="Toggle theme">
              {theme === 'light' ? <FiMoon /> : <FiSun />}
            </button>
            <button onClick={handleLogout} className="logout-btn" title="Logout">
              <FiLogOut /> <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="layout-body">
        <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-brand">
            <div className="logo-container">
              <img
                src="/logo/css-farms-tvet-logo.png"
                alt="CSS FARMS IDEAS-TVET Logo"
                className="logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <div className="logo-text">
              <h1>CSS FARMS IDEAS-TVET</h1>
              <p>Assessor Portal</p>
            </div>
            <button
              className="sidebar-collapse-btn"
              onClick={toggleCollapsed}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <FiChevronLeft />
            </button>
          </div>

          <nav className="nav">
            <div className="nav-group">
              <span className="nav-group-label">Overview</span>
              <button
                className={`nav-link unit-link ${view === 'report' ? 'active' : ''}`}
                onClick={() => setView('report')}
                title={collapsed ? 'My Report' : undefined}
              >
                <FiBarChart2 /> <span>My Report</span>
              </button>
            </div>
            <div className="nav-group">
              <span className="nav-group-label">Units</span>
              {units.map((u) => (
                <button
                  key={u}
                  className={`nav-link unit-link ${view === 'units' && selectedUnit === u ? 'active' : ''}`}
                  onClick={() => { setView('units'); setSelectedUnit(u); }}
                  title={collapsed ? `Unit ${u}` : undefined}
                >
                  <FiCheckSquare /> <span>Unit {u}</span>
                </button>
              ))}
            </div>
          </nav>

          <div className="sidebar-footer">
            <div className="sidebar-account">
              <div className="sidebar-avatar">{initials}</div>
              <div className="sidebar-account-info">
                <strong>{assessor?.name || 'Assessor'}</strong>
                <span>{assessor?.specialization || 'Assessor'}</span>
              </div>
            </div>
          </div>
        </aside>

        <main className="main-content">
          {view === 'report' ? (
            <div className="evidence-book">
              <div className="evidence-book-header">
                <h1>My Report</h1>
                {assessor?.specialization && <span className="chip">{assessor.specialization}</span>}
              </div>

              <div className="report-filters">
                <div className="form-group">
                  <label>Unit</label>
                  <select value={reportUnit} onChange={(e) => setReportUnit(e.target.value)}>
                    <option value="all">All Units</option>
                    {units.map((u) => <option key={u} value={u}>Unit {u}</option>)}
                  </select>
                </div>
              </div>

              {reportLoading || !report ? (
                <div className="loading">Loading report...</div>
              ) : (
                <>
                  <div className="report-totals">
                    <div className="stat-tile"><span>{report.totals.total}</span><small>Total records</small></div>
                    <div className="stat-tile ok"><span>{report.totals.complete}</span><small>Complete</small></div>
                    <div className="stat-tile warn"><span>{report.totals.partial}</span><small>Partial</small></div>
                    <div className="stat-tile muted"><span>{report.totals.none}</span><small>Not started</small></div>
                    <div className="stat-tile"><span>{report.totals.completion_rate}%</span><small>Completion rate</small></div>
                  </div>
                  <div className="assessment-table-wrap">
                    <table className="assessment-table">
                      <thead>
                        <tr>
                          <th>Trainee</th><th>Complete</th><th>Partial</th><th>Not started</th><th>Completion %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.trainees.length === 0 ? (
                          <tr><td colSpan={5}>No trainees are currently assigned to you.</td></tr>
                        ) : report.trainees.map((t) => (
                          <tr key={t.id}>
                            <td data-label="Trainee">{t.name}</td>
                            <td data-label="Complete">{t.complete}</td>
                            <td data-label="Partial">{t.partial}</td>
                            <td data-label="Not started">{t.none}</td>
                            <td data-label="Completion %">{t.completion_rate}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="evidence-book">
              <div className="evidence-book-header">
                <h1>CSS Evidence Book — Unit {selectedUnit}</h1>
                {assessor?.specialization && <span className="chip">{assessor.specialization}</span>}
              </div>

              {loading ? (
                <div className="loading">Loading trainees...</div>
              ) : trainees.length === 0 ? (
                <div className="assessment-empty">
                  <p>No trainees are currently assigned to you. Contact the administrator if this seems wrong.</p>
                </div>
              ) : (
                <div className="assessment-table-wrap">
                  <table className="assessment-table evidence-table">
                    <thead>
                      <tr>
                        <th>Names</th>
                        {FIELDS.map((f) => <th key={f.key} className="checkbox-col">{f.label}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {trainees.map((t) => (
                        <tr key={t.id}>
                          <td data-label="Names">{t.name}</td>
                          {FIELDS.map((f) => (
                            <td key={f.key} data-label={f.label} className="checkbox-col">
                              <input
                                type="checkbox"
                                checked={!!t[f.key]}
                                disabled={savingKey === `${t.id}-${f.key}`}
                                onChange={() => toggleField(t, f.key)}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AssessorDashboard;
